import React, { useState, useEffect } from 'react';
import { Typography, Button, Space, Card, Row, Col, Tabs } from 'antd';
import { EyeOutlined, ShoppingCartOutlined, HeartOutlined } from '@ant-design/icons';
import MarketplaceMenuCard from '../../UI/MarketplaceMenuCard';
import MarketplaceVendorCard from '../../UI/MarketplaceVendorCard';
import MarketplaceGrid from '../../UI/MarketplaceGrid';
import FeaturedSection from '../../UI/FeaturedSection';
import api from '../../../services/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const MarketplaceDemo = () => {
  const [menus, setMenus] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    loadDemoData();
  }, []);

  const loadDemoData = async () => {
    try {
      setLoading(true);
      
      const [menusRes, vendorsRes] = await Promise.all([
        api.get('/menus', { params: { limit: 8 } }),
        api.get('/vendors', { params: { limit: 6 } })
      ]);

      if (menusRes.data.success) {
        setMenus(menusRes.data.data.data || menusRes.data.data);
      }

      if (vendorsRes.data.success) {
        setVendors(vendorsRes.data.data.data || vendorsRes.data.data);
      }
    } catch (error) {
      console.error('Error loading demo data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (menu) => {
    console.log('Add to cart:', menu);
  };

  const handleToggleFavorite = (item) => {
    const newFavorites = new Set(favorites);
    if (favorites.has(item.id)) {
      newFavorites.delete(item.id);
    } else {
      newFavorites.add(item.id);
    }
    setFavorites(newFavorites);
  };

  const handleViewDetails = (item) => {
    console.log('View details:', item);
  };

  const renderMenuCard = (menu) => (
    <MarketplaceMenuCard
      menu={menu}
      onAddToCart={handleAddToCart}
      onViewDetails={handleViewDetails}
      onToggleFavorite={handleToggleFavorite}
      isFavorite={favorites.has(menu.id)}
      showAddToCart={true}
      showFavorite={true}
      showVendor={true}
      imageHeight={200}
    />
  );

  const renderVendorCard = (vendor) => (
    <MarketplaceVendorCard
      vendor={vendor}
      onViewDetails={handleViewDetails}
      onToggleFavorite={handleToggleFavorite}
      isFavorite={favorites.has(vendor.id)}
      showFavorite={true}
      showStats={true}
      imageHeight={180}
    />
  );

  return (
    <div style={{ padding: '24px', background: '#fafafa', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={1} style={{ textAlign: 'center', marginBottom: '48px' }}>
          Marketplace Components Demo
        </Title>

        <Tabs defaultActiveKey="1" size="large">
          <TabPane tab="Menu Cards" key="1">
            <div style={{ marginBottom: '32px' }}>
              <Title level={2}>Menu Cards</Title>
              <Text type="secondary">
                Modern marketplace-style menu cards with hover effects, ratings, and quick actions.
              </Text>
            </div>

            <MarketplaceGrid
              items={menus}
              renderItem={renderMenuCard}
              loading={loading}
              emptyMessage="No menu items found"
              showPagination={false}
              showFilters={false}
              showSearch={false}
              showSort={false}
              gridProps={{
                xs: 24,
                sm: 12,
                md: 8,
                lg: 6,
                xl: 6
              }}
            />
          </TabPane>

          <TabPane tab="Vendor Cards" key="2">
            <div style={{ marginBottom: '32px' }}>
              <Title level={2}>Vendor Cards</Title>
              <Text type="secondary">
                Restaurant and vendor cards with verification badges, ratings, and stats.
              </Text>
            </div>

            <MarketplaceGrid
              items={vendors}
              renderItem={renderVendorCard}
              loading={loading}
              emptyMessage="No vendors found"
              showPagination={false}
              showFilters={false}
              showSearch={false}
              showSort={false}
              gridProps={{
                xs: 24,
                sm: 12,
                md: 8,
                lg: 6,
                xl: 6
              }}
            />
          </TabPane>

          <TabPane tab="Featured Sections" key="3">
            <div style={{ marginBottom: '32px' }}>
              <Title level={2}>Featured Sections</Title>
              <Text type="secondary">
                Carousel-style featured sections for highlighting popular items.
              </Text>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <FeaturedSection
                title="Featured Dishes"
                subtitle="Our most popular and delicious menu items"
                items={menus.slice(0, 6)}
                type="menu"
                loading={loading}
                showViewAll={false}
              />
            </div>

            <div style={{ marginBottom: '48px' }}>
              <FeaturedSection
                title="Top Restaurants"
                subtitle="Highly rated restaurants near you"
                items={vendors.slice(0, 4)}
                type="vendor"
                loading={loading}
                showViewAll={false}
              />
            </div>
          </TabPane>

          <TabPane tab="Grid Layout" key="4">
            <div style={{ marginBottom: '32px' }}>
              <Title level={2}>Grid Layout with Filters</Title>
              <Text type="secondary">
                Complete marketplace grid with search, filters, sorting, and pagination.
              </Text>
            </div>

            <MarketplaceGrid
              items={menus}
              renderItem={renderMenuCard}
              loading={loading}
              emptyMessage="No menu items found"
              showPagination={true}
              showFilters={true}
              showSearch={true}
              showSort={true}
              itemsPerPage={8}
              currentPage={1}
              totalItems={menus.length}
              onSearch={(search) => console.log('Search:', search)}
              onFilter={(filters) => console.log('Filter:', filters)}
              onSort={(sort) => console.log('Sort:', sort)}
              onPageChange={(page) => console.log('Page:', page)}
              searchPlaceholder="Search menu items..."
              sortOptions={[
                { value: 'name', label: 'Name A-Z' },
                { value: 'price', label: 'Price Low to High' },
                { value: 'rating', label: 'Rating' }
              ]}
              filterOptions={[
                {
                  key: 'category',
                  placeholder: 'Category',
                  options: [
                    { value: '1', label: 'North Indian' },
                    { value: '2', label: 'South Indian' },
                    { value: '3', label: 'Chinese' }
                  ]
                }
              ]}
              gridProps={{
                xs: 24,
                sm: 12,
                md: 8,
                lg: 6,
                xl: 6
              }}
            />
          </TabPane>

          <TabPane tab="Features" key="5">
            <div style={{ marginBottom: '32px' }}>
              <Title level={2}>Component Features</Title>
              <Text type="secondary">
                Key features and capabilities of the marketplace components.
              </Text>
            </div>

            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Card title="Menu Card Features" style={{ height: '100%' }}>
                  <ul>
                    <li>Hover effects with smooth animations</li>
                    <li>Image gallery with zoom functionality</li>
                    <li>Rating display with review count</li>
                    <li>Dietary tags (Veg, Vegan, Gluten-free, Spicy)</li>
                    <li>Preparation time display</li>
                    <li>Price with portion information</li>
                    <li>Add to cart and favorite actions</li>
                    <li>Quick view functionality</li>
                    <li>Vendor information display</li>
                    <li>Responsive design for all screen sizes</li>
                  </ul>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card title="Vendor Card Features" style={{ height: '100%' }}>
                  <ul>
                    <li>Cover image with logo overlay</li>
                    <li>Verification badge for trusted vendors</li>
                    <li>Status indicators (Open, Closed, Busy)</li>
                    <li>Rating and review count</li>
                    <li>Cuisine type tags</li>
                    <li>Delivery time and min order info</li>
                    <li>Contact information display</li>
                    <li>Favorite functionality</li>
                    <li>Quick stats section</li>
                    <li>Responsive grid layout</li>
                  </ul>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card title="Grid Layout Features" style={{ height: '100%' }}>
                  <ul>
                    <li>Responsive grid system</li>
                    <li>Search functionality</li>
                    <li>Advanced filtering options</li>
                    <li>Multiple sorting options</li>
                    <li>Pagination with page info</li>
                    <li>Loading states</li>
                    <li>Empty state handling</li>
                    <li>Carousel mode for featured items</li>
                    <li>Customizable grid props</li>
                    <li>Mobile-optimized layout</li>
                  </ul>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card title="Technical Features" style={{ height: '100%' }}>
                  <ul>
                    <li>Built with React and Ant Design</li>
                    <li>TypeScript support</li>
                    <li>Custom CSS animations</li>
                    <li>Image optimization</li>
                    <li>Lazy loading support</li>
                    <li>Accessibility features</li>
                    <li>SEO-friendly structure</li>
                    <li>Performance optimized</li>
                    <li>Cross-browser compatible</li>
                    <li>Easy customization</li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default MarketplaceDemo;
